import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

import { useApiRequest } from '@peanuds/composables';
import {
  createGenericTransactionDetailsRequestTemplate,
  createIncomingTransferDetailsRequestTemplate,
  createOutgoingTransferDetailsRequestTemplate,
  peanudsApi,
  withAuthenticatedHeaders,
  type ExportGenericTransactionConfirmationData,
  type ExportIncomingTransferConfirmationData,
  type ExportOutgoingTransferConfirmationData,
} from '@peanuds/shared';

import type { TransactionDetailsDialogData } from '../transaction-details.types';
import {
  mapGenericTransferDetailsToDialogData,
  mapIncomingTransferDetailsToDialogData,
  mapOutgoingTransferDetailsToDialogData,
} from './transaction-details-dialog.adapter';
import type { TransactionDetailsActiveSelection } from './useTransactionDetailsSelection';

type TransactionDetailsRequestParams = TransactionDetailsActiveSelection & {
  headers?: Record<string, string>;
};

type TransactionStatementFile = {
  blob: Blob;
  fileName: string;
};

interface UseTransactionDetailsDialogDataOptions {
  selection: MaybeRefOrGetter<TransactionDetailsActiveSelection | null>;
}

const incomingTransferDetailsRequestTemplate = createIncomingTransferDetailsRequestTemplate();
const outgoingTransferDetailsRequestTemplate = createOutgoingTransferDetailsRequestTemplate();
const genericTransferDetailsRequestTemplate = createGenericTransactionDetailsRequestTemplate();

const getStatementFileName = (
  contentDisposition: string | undefined,
  transactionId: string,
): string => {
  const utf8Match = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const quotedMatch = contentDisposition?.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = contentDisposition?.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) return plainMatch[1].trim();

  return `transaction-statement-${transactionId}.pdf`;
};

const getIncomingTransfer = async (transactionId: string) => {
  const response = await incomingTransferDetailsRequestTemplate.request({
    headers: { accept: 'application/json' },
    path: { id: transactionId },
  });

  if (response instanceof Error) return response;

  return {
    data: mapIncomingTransferDetailsToDialogData(response.data),
    error: undefined,
  } as const;
};

const getOutgoingTransfer = async (transactionId: string) => {
  const response = await outgoingTransferDetailsRequestTemplate.request({
    headers: { accept: 'application/json' },
    path: { id: transactionId },
  });

  if (response instanceof Error) return response;

  return {
    data: mapOutgoingTransferDetailsToDialogData(response.data),
    error: undefined,
  } as const;
};

const getGenericTransfer = async (
  transactionId: string,
  direction: TransactionDetailsActiveSelection['direction'],
) => {
  const response = await genericTransferDetailsRequestTemplate.request({
    headers: { accept: 'application/json' },
    path: { id: transactionId },
  });

  if (response instanceof Error) return response;

  return {
    data: mapGenericTransferDetailsToDialogData(response.data, direction),
    error: undefined,
  } as const;
};

const createTransactionStatementFile = (
  blob: Blob,
  contentDisposition: unknown,
  transactionId: string,
): TransactionStatementFile => {
  const contentDispositionHeader =
    typeof contentDisposition === 'string' ? contentDisposition : undefined;

  return {
    blob,
    fileName: getStatementFileName(contentDispositionHeader, transactionId),
  };
};

const getIncomingTransferStatement = async (transactionId: string) => {
  const requestOptions = await withAuthenticatedHeaders<
    ExportIncomingTransferConfirmationData,
    true
  >({
    path: { id: transactionId },
    headers: { accept: 'application/pdf' },
    responseType: 'blob',
    throwOnError: true,
  });
  const response = await peanudsApi.exportIncomingTransferConfirmation<true>(requestOptions);

  if (!(response.data instanceof Blob)) {
    throw new Error('Incoming transfer statement response is not a downloadable file.');
  }

  return createTransactionStatementFile(
    response.data,
    response.headers?.['content-disposition'],
    transactionId,
  );
};

const getOutgoingTransferStatement = async (transactionId: string) => {
  const requestOptions = await withAuthenticatedHeaders<
    ExportOutgoingTransferConfirmationData,
    true
  >({
    path: { id: transactionId },
    headers: { accept: 'application/pdf' },
    responseType: 'blob',
    throwOnError: true,
  });
  const response = await peanudsApi.exportOutgoingTransferConfirmation<true>(requestOptions);

  if (!(response.data instanceof Blob)) {
    throw new Error('Outgoing transfer statement response is not a downloadable file.');
  }

  return createTransactionStatementFile(
    response.data,
    response.headers?.['content-disposition'],
    transactionId,
  );
};

const getGenericTransferStatement = async (transactionId: string) => {
  const requestOptions = await withAuthenticatedHeaders<
    ExportGenericTransactionConfirmationData,
    true
  >({
    path: { id: transactionId },
    headers: { accept: 'application/pdf' },
    responseType: 'blob',
    throwOnError: true,
  });
  const response = await peanudsApi.exportGenericTransactionConfirmation<true>(requestOptions);

  if (!(response.data instanceof Blob)) {
    throw new Error('Generic transfer statement response is not a downloadable file.');
  }

  return createTransactionStatementFile(
    response.data,
    response.headers?.['content-disposition'],
    transactionId,
  );
};

export const useTransactionDetailsDialogData = ({
  selection,
}: UseTransactionDetailsDialogDataOptions) => {
  const isDownloadingStatement = ref(false);
  const transactionDetailsRequest = useApiRequest({
    concurrency: { strategy: 'latest-wins' },
    request: async ({
      direction,
      transactionId,
      transactionSource,
    }: TransactionDetailsRequestParams) => {
      if (transactionSource === 'INCOMING_TRANSFER') return getIncomingTransfer(transactionId);
      if (transactionSource === 'OUTGOING_TRANSFER') return getOutgoingTransfer(transactionId);

      return getGenericTransfer(transactionId, direction);
    },
  });

  const { data, error, execute, invalidate, isLoading, reset } = transactionDetailsRequest;

  const clearDetailState = () => {
    invalidate();
    reset();
  };

  watch(
    () => toValue(selection),
    async (currentSelection, _previousSelection, onCleanup) => {
      clearDetailState();

      if (!currentSelection) return;

      onCleanup(() => {
        invalidate();
      });

      await execute(currentSelection);
    },
    { immediate: true },
  );

  const dialogData = computed<TransactionDetailsDialogData | null>(() => {
    return data.value ?? null;
  });

  const downloadStatement = async (): Promise<TransactionStatementFile | null> => {
    const currentSelection = toValue(selection);

    if (!currentSelection) return null;

    isDownloadingStatement.value = true;

    try {
      if (currentSelection.transactionSource === 'INCOMING_TRANSFER') {
        return await getIncomingTransferStatement(currentSelection.transactionId);
      }

      if (currentSelection.transactionSource === 'OUTGOING_TRANSFER') {
        return await getOutgoingTransferStatement(currentSelection.transactionId);
      }

      return await getGenericTransferStatement(currentSelection.transactionId);
    } catch {
      return null;
    } finally {
      isDownloadingStatement.value = false;
    }
  };

  return {
    dialogData,
    downloadStatement,
    error,
    isLoading,
    isDownloadingStatement,
  };
};
